/**
 * Purchase View Component
 * Handles plan/top-up purchase with Zarinpal and Bale Wallet payment methods
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../services/apiClient';
import { baleWebApp } from '../services/baleWebApp';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';

type PaymentMethod = 'zarinpal' | 'bale_wallet';
type PurchaseStatus = 'idle' | 'processing' | 'success' | 'error';

export const PurchaseView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const planId = searchParams.get('plan_id');
  const purchaseType = searchParams.get('type') === 'top_up' ? 'top_up' : 'subscription';
  const credits = searchParams.get('credits');
  const paymentStatus = searchParams.get('payment') || searchParams.get('status');
  
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>('zarinpal');
  const [status, setStatus] = useState<PurchaseStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const handleZarinpalCallback = React.useCallback(async (status: string) => {
    if (status === 'OK' || status === 'success' || status === 'already_completed') {
      setStatus('success');
      baleWebApp.showAlert('پرداخت با موفقیت انجام شد! اعتبار شما به‌روزرسانی شد.', () => {
        navigate('/balance');
      });
    } else {
      setStatus('error');
      setError('پرداخت لغو شد یا با خطا مواجه شد.');
    }
  }, [navigate]);

  useEffect(() => {
    // Handle Zarinpal callback
    if (paymentStatus) {
      handleZarinpalCallback(paymentStatus);
    }
  }, [handleZarinpalCallback, paymentStatus]);

  const handlePurchase = async () => {
    if (purchaseType === 'subscription' && !planId) {
      setError('شناسه پلن مشخص نشده است');
      return;
    }
    if (purchaseType === 'top_up' && !credits) {
      setError('مقدار اعتبار مشخص نشده است');
      return;
    }

    try {
      setStatus('processing');
      setError(null);

      const response = await apiClient.initiatePurchase({
        type: purchaseType,
        plan_id: planId || '',
        credits: credits ? Number(credits) : undefined,
        payment_method: selectedMethod,
      });

      if (selectedMethod === 'zarinpal') {
        // Redirect to Zarinpal gateway
        if (response.payment_url) {
          window.location.href = response.payment_url;
        } else {
          throw new Error('آدرس درگاه پرداخت دریافت نشد');
        }
      } else if (selectedMethod === 'bale_wallet') {
        // Handle Bale Wallet payment
        if (response.invoice_message_id) {
          await handleBaleWalletPayment(response.transaction_id);
        } else {
          throw new Error('شناسه فاکتور دریافت نشد');
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'خطا در شروع پرداخت';
      setError(message);
      setStatus('error');
    }
  };

  const handleBaleWalletPayment = async (transactionId: string) => {
    try {
      // Show message to user
      baleWebApp.showAlert(
        'لطفاً فاکتور را در پیام‌رسان بله تأیید کنید. پس از تأیید، وضعیت پرداخت به‌روزرسانی می‌شود.',
        () => {
          // Start polling for transaction status
          pollTransactionStatus(transactionId);
        }
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'خطا در پرداخت با کیف پول بله';
      setError(message);
      setStatus('error');
    }
  };

  const pollTransactionStatus = async (transactionId: string) => {
    let attempts = 0;
    const maxAttempts = 30; // Poll for up to 5 minutes (30 * 10 seconds)
    
    const poll = async () => {
      try {
        // Get transaction status from transactions endpoint
        const transactions = await apiClient.getTransactions(1, 1);
        const transaction = transactions.transactions.find(t => t.id === transactionId);
        
        if (transaction) {
          if (transaction.status === 'completed') {
            setStatus('success');
            baleWebApp.showAlert('پرداخت با موفقیت انجام شد! اعتبار شما به‌روزرسانی شد.', () => {
              navigate('/balance');
            });
            return;
          } else if (transaction.status === 'failed' || transaction.status === 'cancelled') {
            setStatus('error');
            setError('پرداخت ناموفق بود یا لغو شد.');
            return;
          }
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          // Exponential backoff: 5s, 10s, 15s, then 10s intervals
          const delay = Math.min(5000 + (attempts * 2500), 10000);
          setTimeout(poll, delay);
        } else {
          setStatus('error');
          setError('زمان انتظار برای تأیید پرداخت به پایان رسید. لطفاً تاریخچه تراکنش‌ها را بررسی کنید.');
        }
      } catch (err) {
        console.error('Error polling transaction status:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000);
        } else {
          setStatus('error');
          setError('خطا در بررسی وضعیت پرداخت. لطفاً تاریخچه تراکنش‌ها را بررسی کنید.');
        }
      }
    };
    
    // Start polling after 5 seconds
    setTimeout(poll, 5000);
  };

  if (purchaseType === 'subscription' && !planId && !paymentStatus) {
    return (
      <div style={styles.container}>
        <ErrorMessage
          message="شناسه پلن مشخص نشده است"
          onRetry={() => navigate('/plans')}
        />
      </div>
    );
  }

  if (purchaseType === 'top_up' && !credits && !paymentStatus) {
    return (
      <div style={styles.container}>
        <ErrorMessage
          message="مقدار اعتبار مشخص نشده است"
          onRetry={() => navigate('/plans')}
        />
      </div>
    );
  }

  if (status === 'processing') {
    return (
      <LoadingSpinner message="در حال پردازش پرداخت..." />
    );
  }

  if (status === 'success') {
    return (
      <div style={styles.successContainer}>
        <div style={styles.successIcon}>✅</div>
        <h2 style={styles.successTitle}>پرداخت موفق!</h2>
        <p style={styles.successText}>
          اعتبار شما با موفقیت شارژ شد.
        </p>
        <button
          onClick={() => navigate('/balance')}
          style={styles.successButton}
        >
          مشاهده موجودی
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>انتخاب روش پرداخت</h2>
        
        {error && <ErrorMessage message={error} />}

        <div style={styles.methodsContainer}>
          {/* Zarinpal */}
          <div
            onClick={() => setSelectedMethod('zarinpal')}
            style={{
              ...styles.methodCard,
              ...(selectedMethod === 'zarinpal' ? styles.methodCardActive : {}),
            }}
          >
            <div style={styles.methodIcon}>💳</div>
            <div style={styles.methodInfo}>
              <h3 style={styles.methodName}>زرین‌پال</h3>
              <p style={styles.methodDescription}>
                پرداخت با کارت‌های بانکی
              </p>
            </div>
            <div style={styles.methodRadio}>
              {selectedMethod === 'zarinpal' && <div style={styles.methodRadioActive} />}
            </div>
          </div>

          {/* Bale Wallet */}
          <div
            onClick={() => setSelectedMethod('bale_wallet')}
            style={{
              ...styles.methodCard,
              ...(selectedMethod === 'bale_wallet' ? styles.methodCardActive : {}),
            }}
          >
            <div style={styles.methodIcon}>👛</div>
            <div style={styles.methodInfo}>
              <h3 style={styles.methodName}>کیف پول بله</h3>
              <p style={styles.methodDescription}>
                پرداخت از طریق کیف پول بله
              </p>
            </div>
            <div style={styles.methodRadio}>
              {selectedMethod === 'bale_wallet' && <div style={styles.methodRadioActive} />}
            </div>
          </div>
        </div>

        <div style={styles.infoBox}>
          <p style={styles.infoText}>
            💡 پس از انتخاب روش پرداخت، به درگاه پرداخت منتقل می‌شوید.
          </p>
          {selectedMethod === 'bale_wallet' && (
            <p style={styles.infoText}>
              📱 برای پرداخت با کیف پول بله، فاکتور در پیام‌رسان بله برای شما ارسال می‌شود.
            </p>
          )}
        </div>

        <button
          onClick={handlePurchase}
          style={styles.purchaseButton}
        >
          ادامه پرداخت
        </button>

        <button
          onClick={() => navigate('/plans')}
          style={styles.cancelButton}
        >
          انصراف
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    paddingBottom: '1rem',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '1.5rem',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: '1.5rem',
    textAlign: 'center',
  },
  methodsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  methodCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  methodCardActive: {
    borderColor: '#646cff',
    backgroundColor: '#f5f5ff',
  },
  methodIcon: {
    fontSize: '2rem',
  },
  methodInfo: {
    flex: 1,
  },
  methodName: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1a1a1a',
    margin: '0 0 0.25rem 0',
  },
  methodDescription: {
    fontSize: '0.75rem',
    color: '#666',
    margin: 0,
  },
  methodRadio: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: '2px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  methodRadioActive: {
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    backgroundColor: '#646cff',
  },
  infoBox: {
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1.5rem',
  },
  infoText: {
    fontSize: '0.875rem',
    color: '#666',
    margin: '0.5rem 0',
    lineHeight: 1.6,
  },
  purchaseButton: {
    width: '100%',
    backgroundColor: '#646cff',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    marginBottom: '0.75rem',
  },
  cancelButton: {
    width: '100%',
    backgroundColor: 'transparent',
    color: '#666',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '0.75rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  successContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '3rem 1.5rem',
    textAlign: 'center',
  },
  successIcon: {
    fontSize: '4rem',
    marginBottom: '1rem',
  },
  successTitle: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#2e7d32',
    margin: '0 0 0.5rem 0',
  },
  successText: {
    fontSize: '0.875rem',
    color: '#666',
    margin: '0 0 2rem 0',
  },
  successButton: {
    backgroundColor: '#4caf50',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 2rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
};
